import Pusher from 'pusher-js';
import { uuid } from '../../application_element/lib/utils.js';
import { pusherChannelCustomHandler } from '../../../lib/pusher_extend.js';

export function hashCode(str) {
  if (!str.length) return 0;

  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

function digest(payload) {
  return hashCode(JSON.stringify(payload));
}

// Be aware if you set this to true tests will fail randomly
const SIMULATE_BAD_CONNECTION = false;

// Pusher.logToConsole = true;

export const pusherConfig = {};

// Use Soketi local server compatible with Pusher protocol
function setupDefaultPusher() {
  const pusher = new Pusher('app-key', {
    wsHost: window.location.hostname,
    wsPort: 6001,
    forceTLS: false,
    encrypted: false,
    disableStats: true,
    enabledTransports: ['ws'],
    cluster: 'eu', // it mandatory but not used locally,
    channelAuthorization: {
      customHandler: (data, callback) =>
        pusherChannelCustomHandler(pusher, data, callback),
    },
  });

  return pusher;
}

function pusher() {
  pusherConfig.pusher ??= window.pusher ?? setupDefaultPusher();
  return pusherConfig.pusher;
}

export const getPusherInstance = pusher;

export const unsentEvents = new Map();

// Retry at most 10 times with interval od 3000ms - That's around 30 seconds
export const MAX_RETRIES = 10;
const RETRY_INTERVAL = 3000;

export function retrySendingUnsentEvents() {
  if (!pusher()) return false;
  if (unsentEvents.size === 0) return false;

  // eslint-disable-next-line no-unused-vars
  for (const [transactionId, event] of unsentEvents) {
    const { eventName, payload, channelName } = event;
    const { transaction } = payload;

    if (transaction.retry >= MAX_RETRIES && !transaction.retryExhausted) {
      transaction.retryExhausted = true;

      document.dispatchEvent(
        new CustomEvent('pusher-retry-exhausted', {
          detail: { eventName, payload, channelName },
        })
      );

      unsentEvents.delete(transactionId);
    }

    if (transaction.retryExhausted) continue;

    // Skip if event is too fresh, it will be processed in next interval
    if (transaction.createdAt + RETRY_INTERVAL > Date.now()) continue;

    console.log('retrySendingUnsentEvents for', transaction);

    // console.log('[ACK] onTick', event);
    transaction.retry ??= 0;
    transaction.retry += 1;

    // Connect to pusher and other channels if not connected
    pusher().connect();

    const channel = pusher().subscribe(channelName);

    // // Add delay so channel is subscribed
    // setTimeout(() => {
    channel.trigger(eventName, payload);
    // }, 0);
  }

  return unsentEvents.size;
}

export function processGlobalEvent(_eventName, data) {
  const { ack, transaction } = data;

  if (ack) {
    if (unsentEvents.delete(ack.id)) {
      console.debug('[ACK] 🟢 RECEIVED FOR TRANSACTION', ack.id);
    }
  }

  if (transaction?.needsAck) {
    const { id } = transaction;
    console.log('[ACK] ▶️ BROADCAST ACKNOWLEDGEMENT FOR TRANSACTION ID', {
      ...data,
      /* c8 ignore next */
      signature: `${data.signature?.slice(0, 20)}...`,
    });
    pusherConfig.globalAckChannel.subscribe();
    pusherConfig.globalAckChannel.trigger('client-ack', { ack: { id } });
  }
}

export function setupPusherGlobalAckSystem() {
  if (pusherConfig.globalAckSystem) return false;

  // The channel needs to be private so that we can send client events from Browser to Browser
  pusherConfig.globalAckChannel = pusher().subscribe('private-global');
  pusherConfig.tick = setInterval(retrySendingUnsentEvents, RETRY_INTERVAL);

  pusher().bind_global(processGlobalEvent);

  pusherConfig.globalAckSystem = true;

  return true;
}

export function addToUnsentEvents({ eventName, payload, channelName }) {
  const { id } = payload.transaction;
  console.log(
    '[ACK] 🟡 PENDING CONFIRMATION FOR TRANSACTION',
    id,
    `${eventName}@${channelName}`
  );
  unsentEvents.set(id, { eventName, payload, channelName });
}

export function removePusherUnsentEventBy({ method, resourceId }) {
  for (const [transactionId, event] of unsentEvents) {
    const { payload } = event;
    if (payload.method == method && payload.resourceId == resourceId) {
      unsentEvents.delete(transactionId);
      return event;
    }
  }
}

export function pusherAckTrigger(
  channel,
  eventName,
  data,
  { needsAck = true, staticTransactionId } = {}
) {
  const id = uuid();
  const payload = {
    ...data,
    transaction: {
      id: staticTransactionId ?? /* c8 ignore next */ id,
      needsAck,
      retry: 0,
      createdAt: Date.now(),
      digest: digest(data),
    },
  };
  const channelName = channel.name;

  if (needsAck) {
    addToUnsentEvents({ eventName, payload, channelName });
  }

  // Simulate bad connection
  /* c8 ignore next 2 */
  if (SIMULATE_BAD_CONNECTION && Date.now() % SIMULATE_BAD_CONNECTION === 0)
    return;

  channel.subscribe();
  return channel.trigger(eventName, payload);
}

export default pusherConfig;
